import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your access...');
  const [email, setEmail] = useState('');

  useEffect(() => {
    let isMounted = true;
    let hasVerified = false;
    
    const verifyMagicLink = async () => {
      // Prevent double execution (React StrictMode)
      if (hasVerified) return;
      hasVerified = true;
      
      const token = searchParams.get('token');
      
      // Debug logging - will be stripped in production builds
      if (process.env.NODE_ENV === 'development') {
        console.debug('AccessPage: Starting verification with token:', token ? 'present' : 'missing');
      }
      
      if (!token) {
        if (isMounted) {
          setStatus('error');
          setMessage('No access token provided. Please check your email for the correct link.');
        }
        return;
      }

      try {
        // Get device fingerprint (simple version for now)
        const fingerprint = getDeviceFingerprint();

        const response = await fetch(`${API_URL}/api/stream/verify-magic-link`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
            device_fingerprint: fingerprint
          }),
        });

        const responseText = await response.text();
        let responseData;
        
        try {
          responseData = JSON.parse(responseText);
        } catch (parseError) {
          console.error('AccessPage: Failed to parse response');
          throw new Error('Invalid server response');
        }
        
        if (!response.ok) {
          throw new Error(responseData.detail || 'Failed to verify access');
        }
        
        // Store access data in localStorage
        const accessData = {
          accessToken: responseData.access_token,
          email: responseData.email,
          expiresAt: responseData.expires_at,
          videoId: responseData.video_id,
          verifiedAt: new Date().toISOString()
        };
        
        localStorage.setItem('streamAccess', JSON.stringify(accessData));
        
        if (isMounted) {
          setEmail(responseData.email);
          setStatus('success');
          setMessage('Access verified! Redirecting to stream...');
          
          toast.success('Welcome! Your stream access is now active.');

          // Redirect to stream page after a short delay
          setTimeout(() => {
            if (isMounted) {
              navigate('/stream');
            }
          }, 2000);
        }

      } catch (error) {
        console.error('AccessPage: Verification failed');
        if (isMounted) {
          setStatus('error');
          // Check for specific error types
          if (error.message?.includes('another device')) {
            setMessage('This link has already been activated on another device. Each purchase allows access on one device only.');
          } else if (error.message?.includes('expired')) {
            setMessage('This link has expired. Magic links are valid for 15 minutes after being sent.');
          } else {
            setMessage(error.message || 'Failed to verify access. The link may have expired or already been used.');
          }
          toast.error('Access verification failed');
        }
      }
    };

    verifyMagicLink();
    
    return () => {
      isMounted = false;
    };
  }, [searchParams, navigate]);

  const getDeviceFingerprint = () => {
    // Simple device fingerprint based on available browser data
    let fingerprint = localStorage.getItem('deviceFingerprint');
    if (!fingerprint) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('washedaway', 2, 2);
      
      const components = [
        navigator.userAgent,
        navigator.language,
        new Date().getTimezoneOffset(),
        screen.width + 'x' + screen.height,
        screen.colorDepth,
        canvas.toDataURL()
      ];
      
      fingerprint = btoa(components.join('|')).substring(0, 32);
      localStorage.setItem('deviceFingerprint', fingerprint);
    }
    return fingerprint;
  };

  return (
    <div className="access-page">
      <div className="access-container">
        <div className="access-card">
          {/* Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_washed-away-cinema/artifacts/zlh4k7vd_LOGO.png" 
            alt="Washed Away"
            className="access-logo"
          />

          {/* Status Icon */}
          <div className="status-icon">
            {status === 'verifying' && (
              <Loader2 className="h-16 w-16 animate-spin text-electric-blue" />
            )}
            {status === 'success' && (
              <CheckCircle className="h-16 w-16 text-green-500" />
            )}
            {status === 'error' && (
              <XCircle className="h-16 w-16 text-red-500" />
            )}
          </div>

          {/* Status Message */}
          <h1 className="access-title">
            {status === 'verifying' && 'Verifying Access'}
            {status === 'success' && 'Access Granted!'}
            {status === 'error' && 'Access Denied'}
          </h1>

          <p className="access-message">{message}</p>

          {status === 'success' && email && (
            <p className="access-email">
              Registered to: <strong>{email}</strong>
            </p>
          )}

          {/* Action Buttons */}
          {status === 'error' && (
            <div className="access-actions">
              <Button
                onClick={() => navigate('/')}
                className="access-button"
                size="lg"
              >
                Return to Home
              </Button>
              <p className="access-help">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Need help? Contact support@washedaway.au
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="access-countdown">
              <p>You have 48 hours of viewing access</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessPage;
